import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { STORAGE_TOKENS } from "../lib/storageTokens";
import { toast } from "@/shared/widgets/toast";
import { getErrorMessage } from "../lib/utils";

interface AuthAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
  suppressRedirectOnFail?: boolean;
  suppressErrorToast?: boolean;
}

interface QueueItem {
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
  config: AuthAxiosRequestConfig;
}

// Estado do refresh de autenticação
let isRefreshing = false;
let failedQueue: QueueItem[] = [];

// ============================================
// UTILITÁRIOS DE AUTENTICAÇÃO
// ============================================

const clearAuthData = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_TOKENS.USER_DATA);
};

const isAuthPage = () => {
  if (typeof window === "undefined") return false;
  return window.location.pathname.includes("/auth");
};

const redirectToAuth = () => {
  if (typeof window === "undefined" || isAuthPage()) return;
  
  clearAuthData();
  window.location.href = "/auth";
};

const dispatchAuthEvent = (eventName: string) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(eventName));
};

const logAuthRedirect = (
  reason: string,
  error?: AxiosError,
  config?: AuthAxiosRequestConfig
) => {
  if (typeof window === "undefined") return;

  const targetConfig = config || (error?.config as AuthAxiosRequestConfig | undefined);
  
  console.warn("[auth] redirecting to /auth", {
    reason,
    requestUrl: targetConfig?.url ?? error?.config?.url,
    requestMethod: (targetConfig?.method ?? error?.config?.method)?.toUpperCase(),
    responseStatus: error?.response?.status,
  });
};

// ============================================
// GERENCIAMENTO DA FILA DE REQUISIÇÕES
// ============================================

const processQueue = (error: Error | null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      client(config).then(resolve).catch(reject);
    }
  });

  failedQueue = [];
};

const addToQueue = (
  resolve: (value: unknown) => void,
  reject: (error: unknown) => void,
  config: AuthAxiosRequestConfig
): Promise<unknown> => {
  return new Promise((res, rej) => {
    failedQueue.push({ 
      resolve: res, 
      reject: rej, 
      config 
    });
  });
};

// ============================================
// TRATAMENTO DE ERROS
// ============================================

const extractErrorMessage = (error: AxiosError): string => {
  const responseData = error.response?.data;
  
  if (typeof responseData === 'object' && responseData !== null) {
    const data = responseData as Record<string, unknown>;
    
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
  }
  
  return getErrorMessage(error);
};

const getStatusMessage = (status: number): { title: string; description: string } => {
  const messages: Record<number, { title: string; description: string }> = {
    400: {
      title: "Requisição inválida",
      description: "Os dados enviados estão incorretos. Verifique e tente novamente.",
    },
    401: {
      title: "Não autorizado",
      description: "Sua sessão expirou. Faça login novamente.",
    },
    403: {
      title: "Acesso negado",
      description: "Você não tem permissão para realizar esta ação.",
    },
    404: {
      title: "Não encontrado",
      description: "O recurso solicitado não foi encontrado.",
    },
    413: {
      title: "Arquivo muito grande",
      description: "O arquivo enviado excede o tamanho máximo permitido. Tente enviar um arquivo menor.",
    },
    422: {
      title: "Dados inválidos",
      description: "Os dados fornecidos não são válidos. Verifique e tente novamente.",
    },
    429: {
      title: "Muitas requisições",
      description: "Você fez muitas requisições. Aguarde um momento e tente novamente.",
    },
    500: {
      title: "Erro no servidor",
      description: "Ocorreu um erro interno. Tente novamente mais tarde.",
    },
    502: {
      title: "Serviço indisponível",
      description: "O serviço está temporariamente indisponível. Tente novamente mais tarde.",
    },
    503: {
      title: "Serviço indisponível",
      description: "O serviço está temporariamente indisponível. Tente novamente mais tarde.",
    },
    504: {
      title: "Serviço indisponível",
      description: "O serviço está temporariamente indisponível. Tente novamente mais tarde.",
    },
  };

  return messages[status] || {
    title: "Erro na requisição",
    description: "Ocorreu um erro ao processar sua solicitação.",
  };
};

const showErrorToast = (status: number, errorMessage: string, suppress?: boolean) => {
  if (typeof window === "undefined" || suppress) return;

  const statusMessage = getStatusMessage(status);
  const description = errorMessage !== statusMessage.description 
    ? errorMessage 
    : statusMessage.description;

  toast.api.error(statusMessage.title, description);
};

// ============================================
// REFRESH DE TOKEN
// ============================================

const refreshAuthToken = async (): Promise<void> => {
  await client.request({
    url: "/v1/auth/refresh-web",
    method: "POST",
    withCredentials: true,
    skipAuthRefresh: true,
  } as AuthAxiosRequestConfig);

  // Aguarda o browser processar o Set-Cookie
  await new Promise((resolve) => setTimeout(resolve, 100));
};

const handleRefreshSuccess = (originalRequest: AuthAxiosRequestConfig) => {
  processQueue(null);
  return client(originalRequest);
};

const handleRefreshFailure = (
  refreshError: unknown,
  originalRequest: AuthAxiosRequestConfig
) => {
  const error = refreshError instanceof Error
    ? refreshError
    : new Error("Unable to refresh session");

  processQueue(error);
  
  logAuthRedirect(
    "refresh flow failed",
    Axios.isAxiosError(refreshError) ? refreshError : undefined,
    originalRequest
  );

  dispatchAuthEvent("auth:refresh-failed");

  if (!originalRequest?.suppressErrorToast) {
    showErrorToast(401, "Sua sessão expirou. Por favor, faça login novamente.");
  }

  redirectToAuth();
  
  return Promise.reject(error);
};

// ============================================
// TRATAMENTO DE 401
// ============================================

const shouldSkipAuthRefresh = (config?: AuthAxiosRequestConfig): boolean => {
  return !config || config.skipAuthRefresh || false;
};

const hasAlreadyRetried = (config: AuthAxiosRequestConfig): boolean => {
  return config._retry || false;
};

const handleUnauthorized = async (
  error: AxiosError,
  originalRequest: AuthAxiosRequestConfig
): Promise<unknown> => {
  // Ignorar requisições que não devem fazer refresh
  if (shouldSkipAuthRefresh(originalRequest)) {
    logAuthRedirect("skipAuthRefresh request or missing config", error, originalRequest);
    return Promise.reject(error);
  }

  // Se já tentou refresh e ainda recebeu 401
  if (hasAlreadyRetried(originalRequest)) {
    if (!originalRequest.suppressRedirectOnFail) {
      logAuthRedirect("request already retried once", error, originalRequest);
      dispatchAuthEvent("auth:refresh-failed");
      redirectToAuth();
    }
    return Promise.reject(error);
  }

  // Marcar como retry para evitar loop infinito
  originalRequest._retry = true;
  originalRequest.suppressRedirectOnFail = true;

  // Se já está fazendo refresh, adicionar à fila
  if (isRefreshing) {
    return addToQueue(
      (value) => Promise.resolve(value),
      (err) => Promise.reject(err),
      originalRequest
    );
  }

  // Iniciar processo de refresh
  isRefreshing = true;

  try {
    await refreshAuthToken();
    return handleRefreshSuccess(originalRequest);
  } catch (refreshError) {
    return handleRefreshFailure(refreshError, originalRequest);
  } finally {
    isRefreshing = false;
  }
};

// ============================================
// CLIENTE AXIOS
// ============================================

const client = Axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    Accept: "application/json, text/plain, */*",
  },
  withCredentials: true,
});

// ============================================
// INTERCEPTOR DE RESPOSTA
// ============================================

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Requisição cancelada: não tratar
    if (error.code === "ERR_CANCELED" || Axios.isCancel?.(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const originalRequest = (error.config || {}) as AuthAxiosRequestConfig;

    // Tratamento de 401 (autenticação)
    if (status === 401) {
      return handleUnauthorized(error, originalRequest);
    }

    // Tratamento de outros erros HTTP
    if (status) {
      const errorMessage = extractErrorMessage(error);
      showErrorToast(status, errorMessage, originalRequest?.suppressErrorToast);
    }

    return Promise.reject(error);
  }
);

// ============================================
// FUNÇÃO DE API
// ============================================

const api = async <T = unknown>(options: AxiosRequestConfig): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await client({
      ...options,
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    // Requisição cancelada: não disparar refresh/logout
    if (axiosError.code === "ERR_CANCELED" || Axios.isCancel?.(axiosError)) {
      return Promise.reject({
        message: axiosError.message,
        code: axiosError.code,
        response: axiosError.response,
      });
    }

    return Promise.reject({
      message: axiosError.message,
      code: axiosError.code,
      response: axiosError.response,
    });
  }
};

export { client };
export default api;